// src/pages/Signin2.jsx - Version Split Screen avec logo agrandi
import React, { useState, useEffect } from "react";
import { Button, Card, Col, Form, Row, Alert, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import TwoFactorVerification from "../components/TwoFactorVerification";
import logo from "../assets/img/logo.jpeg";
import bgImage from "../assets/img/image.jpg";
import { COLORS } from "../constants/colors";

export default function Signin2() {
  const navigate = useNavigate();
  const { 
    login, 
    verifyTwoFactorCode, 
    resendTwoFactorCode,
    cancelTwoFactor, 
    twoFactorRequired, 
    twoFactorData 
  } = useAuth();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (twoFactorRequired && twoFactorData) {
      console.log('🎯 2FA requis, affichage du modal');
    }
  }, [twoFactorRequired, twoFactorData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Veuillez remplir tous les champs");
      return;
    }
    setLoading(true);
    setError("");
    const result = await login(email, password);
    if (result.success) {
      if (result.requiresTwoFactor) {
        // Le hook va automatiquement mettre twoFactorRequired à true
      } else {
        navigate("/accueil");
      }
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const handleTwoFactorVerify = async (code) => {
    setTwoFactorLoading(true);
    setError('');
    const result = await verifyTwoFactorCode(twoFactorData.userId, code);
    setTwoFactorLoading(false);
    if (result.success) {
      navigate("/accueil");
    }
    return result;
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    const result = await resendTwoFactorCode(twoFactorData.userId);
    setResendLoading(false);
    return result;
  };

  const handleTwoFactorClose = () => {
    cancelTwoFactor();
  };

  return (
    <>
      <style>{`
        .page-sign-split {
          min-height: 100vh;
          display: flex;
          align-items: stretch;
          background: #ffffff;
        }

        /* ===== PARTIE GAUCHE AVEC IMAGE DE FOND ===== */
        .split-left {
          flex: 1;
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 3rem;
          color: white;
        }

        .split-left .bg-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 0;
        }

        .split-left .overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, ${COLORS.primary}90 0%, ${COLORS.primaryDark}85 100%);
          z-index: 1;
        }

        .split-left .brand {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 400px;
        }

        .split-left .brand .icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          display: block;
          text-shadow: 0 4px 20px rgba(0,0,0,0.2);
        }

        .split-left .brand h1 {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
          text-shadow: 0 2px 20px rgba(0,0,0,0.15);
        }

        .split-left .brand p {
          opacity: 0.9;
          max-width: 300px;
          margin: 0 auto;
          line-height: 1.6;
          text-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .split-left .brand .stats {
          display: flex;
          gap: 2rem;
          margin-top: 2rem;
          justify-content: center;
        }

        .split-left .brand .stats div {
          text-align: center;
        }

        .split-left .brand .stats .number {
          font-size: 1.8rem;
          font-weight: 700;
          text-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .split-left .brand .stats .label {
          font-size: 0.8rem;
          opacity: 0.8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* ===== PARTIE DROITE ===== */
        .split-right {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          background: white;
        }

        .login-card-split {
          max-width: 400px;
          width: 100%;
        }

        /* ===== LOGO AVEC TEXTE EN DESSOUS ===== */
        .logo-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          margin-bottom: 2rem;
          padding: 0.8rem 1.2rem;
          background: ${COLORS.primary}04;
          border-radius: 16px;
          border: 1px solid ${COLORS.primary}10;
          transition: all 0.3s ease;
          text-align: center;
        }

        .logo-wrap:hover {
          background: ${COLORS.primary}08;
          border-color: ${COLORS.primary}20;
        }

        .logo-wrap img {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid ${COLORS.primary}20;
          box-shadow: 0 8px 24px rgba(41,90,167,0.15);
          margin-bottom: 0.8rem;
          transition: transform 0.3s ease;
        }

        .logo-wrap img:hover {
          transform: scale(1.05);
        }

        .logo-wrap .brand-name {
          font-weight: 700;
          font-size: 1.4rem;
          color: ${COLORS.primaryDark};
          letter-spacing: -0.3px;
        }

        .logo-wrap .brand-name .highlight {
          color: ${COLORS.secondary};
        }

        .login-card-split h2 {
          font-size: 1.6rem;
          font-weight: 700;
          color: ${COLORS.primaryDark};
          margin-bottom: 0.3rem;
        }

        .login-card-split .sub {
          color: #94a3b8;
          margin-bottom: 2rem;
        }

        .form-control-split {
          border: 1.5px solid #e2e8f0;
          border-radius: 12px !important;
          padding: 0.7rem 1rem;
          font-size: 0.95rem;
          transition: all 0.3s ease;
        }

        .form-control-split:focus {
          border-color: ${COLORS.primary};
          box-shadow: 0 0 0 3px ${COLORS.primary}15;
        }

        .form-label-split {
          font-weight: 600;
          color: ${COLORS.primaryDark};
          font-size: 0.85rem;
          margin-bottom: 0.3rem;
        }

        .btn-split {
          background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark});
          border: none;
          border-radius: 12px !important;
          padding: 0.8rem;
          font-weight: 600;
          font-size: 1rem;
          color: white;
          transition: all 0.3s ease;
          width: 100%;
          box-shadow: 0 4px 16px rgba(41,90,167,0.2);
        }

        .btn-split:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(41,90,167,0.3);
        }

        .btn-split:disabled {
          opacity: 0.6;
        }

        .password-wrap {
          position: relative;
        }

        .password-input-split {
          padding-right: 44px !important;
        }

        .password-toggle-split {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .password-toggle-split:hover {
          color: ${COLORS.primary};
        }

        .forgot-split {
          text-align: right;
          margin-top: 0.3rem;
        }

        .forgot-split a {
          font-size: 0.8rem;
          color: ${COLORS.primary};
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .forgot-split a:hover {
          color: ${COLORS.primaryDark};
          text-decoration: underline;
        }

        .footer-split {
          text-align: center;
          margin-top: 1.5rem;
          font-size: 0.9rem;
          color: #94a3b8;
        }

        .footer-split a {
          color: ${COLORS.primary};
          font-weight: 600;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .footer-split a:hover {
          color: ${COLORS.primaryDark};
          text-decoration: underline;
        }

        .divider-split {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin: 1.2rem 0;
        }

        .divider-split::before,
        .divider-split::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e2e8f0;
        }

        .divider-split span {
          color: #94a3b8;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 992px) {
          .page-sign-split {
            flex-direction: column;
          }
          .split-left {
            padding: 2.5rem;
            min-height: 280px;
          }
          .split-left .brand h1 {
            font-size: 2rem;
          }
          .split-left .brand .stats {
            gap: 1.5rem;
          }
          .split-left .brand .stats .number {
            font-size: 1.4rem;
          }
          .split-left .brand .icon {
            font-size: 3rem;
          }
        }

        @media (max-width: 768px) {
          .split-left {
            min-height: 220px;
            padding: 2rem;
          }
          .split-left .brand p {
            display: none;
          }
          .split-left .brand .stats {
            gap: 1rem;
          }
          .split-left .brand .stats .number {
            font-size: 1.2rem;
          }
          .split-left .brand .stats .label {
            font-size: 0.7rem;
          }
        }

        @media (max-width: 576px) {
          .split-left {
            min-height: 180px;
            padding: 1.5rem;
          }
          .split-left .brand .icon {
            font-size: 2.2rem;
            margin-bottom: 0.5rem;
          }
          .split-left .brand h1 {
            font-size: 1.4rem;
          }
          .split-left .brand .stats {
            gap: 0.8rem;
            margin-top: 1rem;
          }
          .split-left .brand .stats .number {
            font-size: 1rem;
          }
          .split-left .brand .stats .label {
            font-size: 0.6rem;
          }
          .split-right {
            padding: 1rem;
          }
          .login-card-split {
            padding: 0.5rem;
          }
          .logo-wrap {
            padding: 0.6rem 1rem;
          }
          .logo-wrap img {
            width: 60px;
            height: 60px;
          }
          .logo-wrap .brand-name {
            font-size: 1.1rem;
          }
          .login-card-split h2 {
            font-size: 1.3rem;
          }
        }
      `}</style>

      <div className="page-sign-split">
        {/* ===== PARTIE GAUCHE AVEC IMAGE DE FOND ===== */}
        <div className="split-left">
          <img src={bgImage} alt="Background" className="bg-image" />
          <div className="overlay"></div>

          <div className="brand">
            <span className="icon">✝</span>
            <h1>Joseph's Business</h1>
            <p>La plateforme de gestion des églises et de leurs activités</p>
            <div className="stats">
              <div>
                <div className="number">150+</div>
                <div className="label">Églises</div>
              </div>
              <div>
                <div className="number">12K+</div>
                <div className="label">Membres</div>
              </div>
              <div>
                <div className="number">98%</div>
                <div className="label">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== PARTIE DROITE - FORMULAIRE ===== */}
        <div className="split-right">
          <div className="login-card-split">
            {/* Logo en haut avec texte en dessous */}
            <div className="logo-wrap">
              <img src={logo} alt="Logo" />
              <span className="brand-name">
                Joseph's <span className="highlight">Business</span>
              </span>
            </div>

            <h2>Connexion</h2>
            <p className="sub">Connectez-vous pour accéder à votre espace</p>

            {error && (
              <Alert variant="danger" className="mb-3 py-2" style={{ borderRadius: '12px', fontSize: '0.85rem' }}>
                <i className="ri-error-warning-line me-2"></i> {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label-split">Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="exemple@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                  className="form-control-split"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="form-label-split">Mot de passe</Form.Label>
                <div className="password-wrap">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Entrez votre mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    className="form-control-split password-input-split"
                  />
                  <button
                    type="button"
                    className="password-toggle-split"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
                <div className="forgot-split">
                  <Link to="/forgot-password">Mot de passe oublié ?</Link>
                </div>
              </Form.Group>

              <Button type="submit" className="btn-split" disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Se connecter"}
              </Button>
            </Form>

            <div className="footer-split">
              Vous n'avez pas de compte ? <Link to="/register">S'inscrire</Link>
            </div>
          </div>
        </div>
      </div>

      <TwoFactorVerification
        show={twoFactorRequired}
        onClose={handleTwoFactorClose}
        onVerify={handleTwoFactorVerify}
        resendTwoFactorCode={handleResendCode}
        emailMasked={twoFactorData?.emailMasked}
        loading={twoFactorLoading}
        resendLoading={resendLoading}
      />
    </>
  );
}